import { createSignal } from "solid-js"
import { requestHealth, type HealthProbeResult } from "@/transport/health"
import { reportMachineRouteSelection, type MachineRouteReport } from "@/transport/fleet"
import { abortable } from "@/utils/abortable"
import { stampRouteReport } from "./route-telemetry"
import type { MachineRouteInput } from "./machine-route-manager"

// Samples every advertised route this often while the machine is selected and
// the tab is visible. Each sample is one small health request per route, and
// the steady traffic keeps every route's connection warm, so a switch to any
// of them does not pay connection setup.
export const routeSampleIntervalMs = 5_000
// Only one sample per this interval is reported to the control plane, so the
// sampling rate does not set its write rate.
export const routeReportIntervalMs = 60_000
// A page load measures once startup requests have had a head start.
export const routeComparisonFirstCheckMs = 5_000
/** Samples kept per machine: about a minute, the window route choice and display use. */
export const routeSampleWindow = 12
const deadlineMs = 5_000

export type RouteComparisonRound = {
  checkedAt: number
  attempts: MachineRouteReport["attempts"]
}
export type RouteComparisonSnapshot = {
  enabled: boolean
  sampling: boolean
  /** Oldest first, at most routeSampleWindow. */
  rounds: RouteComparisonRound[]
}

// Every advertised provider is measured once there are at least two to compare.
function comparisonKey(input?: MachineRouteInput) {
  const routes = input?.public_routes
  return input && !input.route_policy && routes && new Set(routes.map(route => route.provider)).size >= 2
    ? JSON.stringify([input.name, routes]) : ""
}

type Options = {
  probe?: typeof requestHealth
  report?: typeof reportMachineRouteSelection
}

export function createRouteComparisonController(options: Options = {}) {
  let machine: MachineRouteInput | undefined
  let key = ""
  let timer: ReturnType<typeof setTimeout> | undefined
  let pending: AbortController | undefined
  let disposed = false
  // When this tab last sampled and reported, so returning to the tab keeps both cadences.
  let lastStart: { key: string; at: number } | undefined
  let lastReport: { key: string; at: number } | undefined
  // Disposable measurements from this browser only; never persisted as fleet state.
  const [samplingKey, setSamplingKey] = createSignal("")
  const [sample, setSample] = createSignal<{ key: string; rounds: RouteComparisonRound[] }>()

  const stop = () => {
    clearTimeout(timer)
    timer = undefined
    pending?.abort()
    pending = undefined
  }
  const schedule = (delay: number) => {
    if (!disposed && machine) timer = setTimeout(() => void run(), delay)
  }
  const firstDelay = () => {
    const due = lastStart?.key === key ? lastStart.at + routeSampleIntervalMs - performance.now() : 0
    return Math.max(routeComparisonFirstCheckMs, due)
  }
  const reportDue = () => lastReport?.key !== key || performance.now() - lastReport.at >= routeReportIntervalMs
  const run = async () => {
    timer = undefined
    const input = machine
    if (!input) return
    lastStart = { key, at: performance.now() }
    const controller = new AbortController()
    pending = controller
    let deadline: ReturnType<typeof setTimeout> | undefined
    try {
      const startedElapsed = Math.floor(performance.now())
      const probes = new AbortController()
      const signal = AbortSignal.any([controller.signal, probes.signal])
      deadline = setTimeout(() => probes.abort(new DOMException("Comparison timed out", "TimeoutError")), deadlineMs)
      const attempts = await Promise.all(input.public_routes!.map(async route => {
        const start = performance.now()
        const result = await abortable((options.probe ?? requestHealth)(route.url, {
          signal, expectedMachineName: input.name,
        }), signal).catch((): HealthProbeResult => ({ healthy: false, reason: signal.aborted ? "timeout" : "network" }))
        return {
          route: "direct-public" as const, provider: route.provider,
          result: result.healthy ? "success" as const : "unavailable" as const,
          latency_ms: Math.max(0, Math.round(performance.now() - start)),
          ...(result.healthy === false ? { reason: result.reason, http_status: result.http_status } : {}),
        }
      }))
      clearTimeout(deadline)
      if (controller.signal.aborted) return
      const sampleKey = comparisonKey(input)
      setSample(previous => ({ key: sampleKey, rounds: [
        ...(previous?.key === sampleKey ? previous.rounds : []),
        { checkedAt: Date.now(), attempts },
      ].slice(-routeSampleWindow) }))
      if (!reportDue()) return
      lastReport = { key, at: performance.now() }
      const report: MachineRouteReport = stampRouteReport({ machine: input.name, kind: "comparison", comparison_id: crypto.randomUUID(), round: 1, started_elapsed_ms: startedElapsed, selected: "none", attempts })
      deadline = setTimeout(() => controller.abort(), deadlineMs)
      await abortable((options.report ?? reportMachineRouteSelection)(report, controller.signal), controller.signal)
    } catch {
      // Telemetry is best-effort; no queues or retries on the connection path.
    } finally {
      clearTimeout(deadline)
      controller.abort()
      if (pending === controller) {
        pending = undefined
        schedule(routeSampleIntervalMs)
      }
    }
  }
  return {
    snapshot(input: MachineRouteInput): RouteComparisonSnapshot {
      const inputKey = comparisonKey(input)
      const current = sample()
      return { enabled: !!inputKey, sampling: !!inputKey && samplingKey() === inputKey,
        rounds: inputKey && current?.key === inputKey ? current.rounds : [] }
    },
    sync(input: MachineRouteInput | undefined, visible: boolean) {
      if (disposed) return
      const nextKey = visible ? comparisonKey(input) : ""
      if (key === nextKey) return
      stop()
      key = nextKey
      setSamplingKey(nextKey)
      machine = nextKey && input ? { ...input, public_routes: input.public_routes!.map(route => ({ ...route })) } : undefined
      schedule(firstDelay())
    },
    dispose() {
      disposed = true
      setSamplingKey("")
      setSample(undefined)
      machine = undefined
      stop()
    },
  }
}
